import React from 'react';

import {
  GAME_CONFIG,
  BUILDINGS_BY_ID,
  Building,
  BuildingType,
  Position,
  PlayerFacing,
  BeltItem,
  ITEMS,
  RESEARCHES,
  BeltType,
} from './constants';
import { useUIState, useMapState } from './contexts/GameStateManager';
import { getGameEngine } from './GameEngine';
import {
  GameMap,
  findBuildingByCharacter,
  getDirectionalBuildingCharacter,
  getBuildingPositions,
  RejectedItem,
  AssemblerInventoryLayer,
  getAssemblerInventory,
  findBuildingCenter,
} from './GameState';
import { createMoveCommand } from './InputCommands';
import { InputManager } from './InputManager';
import {
  getNextResearchRequirements,
  getResearchProgress,
  getBeltCapacityMultiplier,
} from './ResearchSystem';
import { getBeltDirectionFromCharacter } from './SmartBeltPlacement';

// Get tile function type
export type GetTileFunction = (
  x: number,
  y: number,
  gameMap: GameMap,
  buildingLayer?: Record<string, string>
) => string | undefined;

// Get target position function type
export type GetTargetPositionFunction = (building?: Building) => Position;

// GameBoard props interface - all props now come from context
type GameBoardProps = Record<string, never>;

// Get CSS class for tile type
export const getTileClass = (tile: string | undefined): string => {
  if (!tile) return 'empty';

  // Check if tile is a building first (including directional buildings)
  const building = findBuildingByCharacter(tile);
  if (building) {
    return building.id; // Use building ID as CSS class (e.g., 'miner', 'drill', 'belt')
  }

  // Otherwise check terrain types
  switch (tile) {
    case GAME_CONFIG.WALL_CHARACTER:
      return 'wall';
    case GAME_CONFIG.TREE_CHARACTER:
      return 'tree';
    case GAME_CONFIG.ROCK_CHARACTER:
      return 'rock';
    case GAME_CONFIG.WATER_CHARACTER:
      return 'water';
    case GAME_CONFIG.SAND_CHARACTER:
      return 'sand';
    case GAME_CONFIG.GRASS_CHARACTER:
      return 'grass';
    case GAME_CONFIG.MOUNTAIN_CHARACTER:
      return 'mountain';
    case GAME_CONFIG.IRON_NODE_CHARACTER:
      return 'iron-node';
    case GAME_CONFIG.COPPER_NODE_CHARACTER:
      return 'copper-node';
    case GAME_CONFIG.BRIDGE_CHARACTER:
      return 'bridge';
    case GAME_CONFIG.EMPTY_CHARACTER:
      return 'empty';
    default:
      return 'empty';
  }
};

// Get human-readable name for tile type
export const getTileName = (tile: string | undefined): string => {
  if (!tile) return 'Unknown';

  // Check if tile is a building first (including directional buildings)
  const building = findBuildingByCharacter(tile);
  if (building) {
    return building.name;
  }

  // Otherwise check terrain types
  switch (tile) {
    case GAME_CONFIG.WALL_CHARACTER:
      return 'Wall';
    case GAME_CONFIG.TREE_CHARACTER:
      return 'Tree';
    case GAME_CONFIG.ROCK_CHARACTER:
      return 'Rock';
    case GAME_CONFIG.WATER_CHARACTER:
      return 'Water';
    case GAME_CONFIG.SAND_CHARACTER:
      return 'Sand';
    case GAME_CONFIG.GRASS_CHARACTER:
      return 'Grass';
    case GAME_CONFIG.MOUNTAIN_CHARACTER:
      return 'Mountain';
    case GAME_CONFIG.IRON_NODE_CHARACTER:
      return 'Iron Node';
    case GAME_CONFIG.COPPER_NODE_CHARACTER:
      return 'Copper Node';
    case GAME_CONFIG.BRIDGE_CHARACTER:
      return 'Land Bridge';
    case GAME_CONFIG.EMPTY_CHARACTER:
      return 'Empty Ground';
    default:
      return 'Unknown';
  }
};

// Multi-layer tile rendering parameters
interface TilePointerInfo {
  position: Position;
  building: Building | null;
  terrainTile: string | undefined;
  isBlocked: boolean;
}

interface MobileActionMenuState {
  position: Position;
  screenX: number;
  screenY: number;
  building: Building | null;
  isBlocked: boolean;
}

interface LongPressInfo extends TilePointerInfo {
  pointerId: number;
  startX: number;
  startY: number;
  lastClientX: number;
  lastClientY: number;
}

interface TouchDragState {
  pointerId: number;
  lastClientX: number;
  lastClientY: number;
}

interface TileRenderParams {
  getTile: GetTileFunction;
  gameMap: Record<string, string>;
  buildingLayer: Record<string, string>;
  beltContentLayer: Record<string, BeltItem[]>;
  assemblerInventoryLayer: AssemblerInventoryLayer;
  rejectedItemsLayer: Record<string, RejectedItem>;
  playerPos: Position;
  targetPos: Position | null;
  buildMode: boolean;
  selectedBuilding: Building | null;
  playerFacing: PlayerFacing;
  getBuildingTargetLocation?: (building: Building) => Position;
  onTileClick?: (position: Position, building: Building | null) => void;
  onTileHover?: (position: Position | null) => void;
  powerOverlayActive: boolean;
  beltCapacityMultiplier: number;
  onTilePointerDown?: (
    event: React.PointerEvent<HTMLDivElement>,
    info: TilePointerInfo
  ) => void;
  onTilePointerUp?: (event: React.PointerEvent<HTMLDivElement>) => void;
  onTilePointerCancel?: (event: React.PointerEvent<HTMLDivElement>) => void;
  onTilePointerLeave?: (event: React.PointerEvent<HTMLDivElement>) => void;
  onTilePointerMove?: (event: React.PointerEvent<HTMLDivElement>) => void;
  suppressTileClick?: React.MutableRefObject<boolean>;
  quickBeltPreviewDirections?: Map<string, PlayerFacing> | undefined;
  quickBeltPreviewBuilding?: Building | null;
  quickBeltPreviewInvalid?: boolean;
}

const LONG_PRESS_DURATION_MS = 500;
const LONG_PRESS_MOVE_TOLERANCE_PX = 12;
const TOUCH_DRAG_MOVE_THRESHOLD_PX = 24;

const isBlockingTerrainTile = (tile: string | undefined): boolean => {
  if (!tile) {
    return false;
  }

  switch (tile) {
    case GAME_CONFIG.WALL_CHARACTER:
    case GAME_CONFIG.TREE_CHARACTER:
    case GAME_CONFIG.ROCK_CHARACTER:
    case GAME_CONFIG.WATER_CHARACTER:
    case GAME_CONFIG.MOUNTAIN_CHARACTER:
      return true;
    default:
      return false;
  }
};

// Render a single tile with all its layers
const renderMultiLayerTile = (
  x: number,
  y: number,
  params: TileRenderParams
): React.ReactElement => {
  const {
    getTile,
    gameMap,
    buildingLayer,
    beltContentLayer,
    assemblerInventoryLayer,
    rejectedItemsLayer,
    playerPos,
    targetPos,
    buildMode,
    selectedBuilding,
    playerFacing,
    getBuildingTargetLocation,
    onTileClick,
    onTileHover,
    powerOverlayActive,
    beltCapacityMultiplier,
    onTilePointerDown,
    onTilePointerUp,
    onTilePointerCancel,
    onTilePointerLeave,
    onTilePointerMove,
    suppressTileClick,
    quickBeltPreviewDirections,
    quickBeltPreviewBuilding,
    quickBeltPreviewInvalid,
  } = params;

  // Get terrain tile (without building layer for base terrain)
  const terrainTile = getTile(x, y, gameMap);

  // Get building and belt items at this position
  const buildingKey = `${x},${y}`;
  let buildingTile = buildingLayer[buildingKey];

  const quickPreviewDirection = quickBeltPreviewDirections?.get(buildingKey);
  const quickPreviewCharacter =
    quickPreviewDirection && quickBeltPreviewBuilding
      ? getDirectionalBuildingCharacter(
          quickBeltPreviewBuilding,
          quickPreviewDirection
        )
      : null;
  const hasQuickBeltPreview =
    Boolean(quickPreviewCharacter) &&
    (quickBeltPreviewDirections?.size ?? 0) > 0;

  // If no building at exact position, check if this position is within a multi-tile building's footprint
  if (!buildingTile) {
    // Check nearby positions for multi-tile building centers (within reasonable range)
    for (let centerY = y - 2; centerY <= y + 2; centerY++) {
      for (let centerX = x - 2; centerX <= x + 2; centerX++) {
        const centerKey = `${centerX},${centerY}`;
        const centerBuilding = buildingLayer[centerKey];
        if (centerBuilding) {
          const building = findBuildingByCharacter(centerBuilding);
          if (
            building &&
            building.size &&
            (building.size.width > 1 || building.size.height > 1)
          ) {
            // Check if current position is within this building's footprint
            const buildingPositions = getBuildingPositions(
              centerX,
              centerY,
              building
            );
            const isWithinFootprint = buildingPositions.some(
              pos => pos.x === x && pos.y === y
            );
            if (isWithinFootprint) {
              buildingTile = centerBuilding;
              break;
            }
          }
        }
      }
      if (buildingTile) break;
    }
  }

  // Check if there's a miner on a resource node - if so, hide the resource node
  const building = buildingTile ? findBuildingByCharacter(buildingTile) : null;
  const buildingCenter = building
    ? findBuildingCenter(x, y, building, buildingLayer)
    : null;
  const isMinerOnResourceNode =
    building &&
    building.category === 'mining' &&
    (terrainTile === GAME_CONFIG.IRON_NODE_CHARACTER ||
      terrainTile === GAME_CONFIG.COPPER_NODE_CHARACTER);

  const handleTileClick = onTileClick
    ? suppressTileClick
      ? (_event: React.MouseEvent<HTMLDivElement>) => {
          if (suppressTileClick.current) {
            suppressTileClick.current = false;
            return;
          }

          onTileClick?.({ x, y }, building ?? null);
        }
      : () => onTileClick?.({ x, y }, building ?? null)
    : undefined;

  const pointerInfo: TilePointerInfo = {
    position: { x, y },
    building: building ?? null,
    terrainTile,
    isBlocked: Boolean(building) || isBlockingTerrainTile(terrainTile),
  };

  // Determine what terrain to show (hide resource nodes under miners)
  const displayTerrainTile = isMinerOnResourceNode ? null : terrainTile;
  const beltItems = beltContentLayer[buildingKey] || [];
  const rejectedItem = rejectedItemsLayer[buildingKey];

  const requiresPower = (building?.powerConsumption ?? 0) > 0;
  const isBuildingCenterTile = building
    ? !building.size ||
      (building.size.width === 1 && building.size.height === 1) ||
      Boolean(
        buildingCenter && buildingCenter.x === x && buildingCenter.y === y
      )
    : false;
  const shouldShowPowerOverlay =
    powerOverlayActive && requiresPower && isBuildingCenterTile;

  // Determine what should be shown at this position
  const isPlayerPosition = x === playerPos.x && y === playerPos.y;
  const isTargetPosition = targetPos && x === targetPos.x && y === targetPos.y;

  // Belt preview logic
  const isBelt =
    selectedBuilding &&
    (selectedBuilding.id === 'belt' || selectedBuilding.id === 'fast_belt');
  const beltPreviewBuilding =
    buildMode && selectedBuilding && isBelt ? selectedBuilding : null;

  let beltPreviewPosition: Position | null = null;
  if (beltPreviewBuilding) {
    if (getBuildingTargetLocation) {
      beltPreviewPosition = getBuildingTargetLocation(beltPreviewBuilding);
    } else if (targetPos) {
      beltPreviewPosition = targetPos;
    } else {
      beltPreviewPosition = playerPos;
    }
  }
  const showBeltPreview =
    !hasQuickBeltPreview &&
    Boolean(
      beltPreviewBuilding &&
        beltPreviewPosition &&
        x === beltPreviewPosition.x &&
        y === beltPreviewPosition.y
    );

  // Non-belt building preview logic - check if current position is within building footprint
  let showBuildingPreview = false;
  if (buildMode && selectedBuilding && !isBelt) {
    // Get the correct target position for building placement
    let buildingTargetPos;
    if (getBuildingTargetLocation) {
      buildingTargetPos = getBuildingTargetLocation(selectedBuilding);
    } else {
      buildingTargetPos = targetPos; // Fallback to basic target position
    }

    if (buildingTargetPos) {
      // Get all positions this building would occupy
      const buildingPositions = getBuildingPositions(
        buildingTargetPos.x,
        buildingTargetPos.y,
        selectedBuilding
      );

      // If the exact same building already exists at the target footprint,
      // skip showing the preview to avoid redundant highlights.
      const isSameBuildingAlreadyPlaced = buildingPositions.every(pos => {
        const existingBuildingChar = buildingLayer[`${pos.x},${pos.y}`];
        if (!existingBuildingChar) {
          return false;
        }
        const existingBuilding = findBuildingByCharacter(existingBuildingChar);
        return existingBuilding?.id === selectedBuilding.id;
      });

      if (!isSameBuildingAlreadyPlaced) {
        // Check if current tile position is within the building's footprint
        showBuildingPreview = buildingPositions.some(
          pos => pos.x === x && pos.y === y
        );
      }
    }
  }

  // Determine highlight class
  let highlightClass = '';
  if (hasQuickBeltPreview) {
    highlightClass = `target-highlight belt-preview-cell quick-belt-preview-cell${
      quickBeltPreviewInvalid ? ' quick-belt-preview-invalid' : ''
    }`;
  } else if (showBeltPreview) {
    highlightClass = 'target-highlight belt-preview-cell';
  } else if (showBuildingPreview) {
    highlightClass = 'target-highlight';
  } else if (isTargetPosition && !buildMode) {
    highlightClass = 'facing-tint';
  }

  return (
    <div
      key={x}
      className={`game-cell ${highlightClass}`}
      data-x={x}
      data-y={y}
      onClick={handleTileClick}
      onMouseEnter={() => onTileHover?.({ x, y })}
      onMouseLeave={() => onTileHover?.(null)}
      onPointerDown={
        onTilePointerDown
          ? event => onTilePointerDown(event, pointerInfo)
          : undefined
      }
      onPointerUp={onTilePointerUp}
      onPointerCancel={onTilePointerCancel}
      onPointerLeave={onTilePointerLeave}
      onPointerMove={onTilePointerMove}
    >
      {/* Layer 1: Terrain */}
      {displayTerrainTile && (
        <span
          className={`tile-layer terrain-layer ${getTileClass(displayTerrainTile)}`}
        >
          {displayTerrainTile}
        </span>
      )}

      {/* Layer 2: Entities */}
      {(() => {
        const entityManager = getGameEngine().getEntityManager();
        const entitiesAtPosition = entityManager.getEntitiesAt({ x, y });
        return entitiesAtPosition.map(entity => (
          <span
            key={entity.id}
            className={`tile-layer entity-layer ${entity.getCSSClass()}`}
            title={entity.getTooltip()}
          >
            {entity.getDisplayChar()}
          </span>
        ));
      })()}

      {/* Layer 3: Buildings */}
      {buildingTile &&
        (() => {
          const buildingConfig = building;
          let displayCharacter = buildingTile;

          const isCraftingBuilding =
            buildingConfig &&
            (buildingConfig.id === BuildingType.ASSEMBLER ||
              buildingConfig.id === BuildingType.FACTORY);

          if (isCraftingBuilding) {
            const centerPosition = buildingCenter;

            if (
              centerPosition &&
              centerPosition.x === x &&
              centerPosition.y === y
            ) {
              const craftingInventory = getAssemblerInventory(
                assemblerInventoryLayer,
                buildingLayer,
                centerPosition
              );

              if (craftingInventory?.recipe) {
                const outputItem = craftingInventory.recipe.outputs[0];
                if (outputItem) {
                  const itemConfig = Object.values(ITEMS).find(
                    item => item.type === outputItem.item
                  );
                  if (itemConfig) {
                    displayCharacter = itemConfig.character;
                  }
                }
              }
            }
          }

          return (
            <span
              className={`tile-layer building-layer ${getTileClass(buildingTile)}`}
            >
              {displayCharacter}
            </span>
          );
        })()}

      {shouldShowPowerOverlay && (
        <span
          className='tile-layer power-alert-overlay'
          title='Insufficient power'
        >
          ⚡️
        </span>
      )}

      {/* Layer 3: Belt Items */}
      {beltItems.length > 0 &&
        beltItems.map((item, _index) => {
          const itemConfig = Object.values(ITEMS).find(
            i => i.type === item.item
          );
          const itemChar = itemConfig?.character || '?';
          const stackCount = item.count ?? 1;

          // Calculate position based on item progress along the belt direction
          const beltDirection = getBeltDirectionFromCharacter(buildingLayer, {
            x,
            y,
          });
          // Offset item along belt while keeping a slight perpendicular offset for centering
          const baseProgress = item.progress - 0.5;
          const horizontalOffset = baseProgress * 200; // ±100% for horizontal belts
          const verticalOffset = baseProgress * 80; // ±40% for vertical belts
          const highStackingActive = beltCapacityMultiplier > 2;
          const perpendicularOffset = highStackingActive ? 3 : 8; // keep tightly centered when stacking is high
          let translateX = 0;
          let translateY = 0;

          switch (beltDirection) {
            case 'right':
              translateX = horizontalOffset - 5;
              translateY = -perpendicularOffset;
              break;
            case 'left':
              translateX = -horizontalOffset;
              translateY = perpendicularOffset;
              break;
            case 'up':
              translateY = -verticalOffset;
              break;
            case 'down':
              translateY = verticalOffset;
              break;
            default:
              translateX = baseProgress * 20;
              translateY = baseProgress * 10;
              break;
          }

          const progressStyle: React.CSSProperties = {
            position: 'relative',
            transform: `translate(${translateX}%, ${translateY}%)`,
          };

          const itemTitle = itemConfig?.name || 'Unknown Item';
          const titleWithCount =
            stackCount > 1 ? `${itemTitle} (x${stackCount})` : itemTitle;

          return (
            <span
              key={item.id}
              className='tile-layer belt-item-layer'
              style={progressStyle}
              title={titleWithCount}
              data-count={stackCount}
            >
              {itemChar}
              {stackCount > 1 && (
                <sup className='belt-item-stack-count'>{stackCount}</sup>
              )}
            </span>
          );
        })}

      {/* Layer 4: Rejected Items */}
      {rejectedItem && (
        <span
          className='tile-layer rejected-item-layer'
          title={`Rejected: ${ITEMS[rejectedItem.itemType.toUpperCase().replace('-', '_') as keyof typeof ITEMS]?.name || rejectedItem.itemType}`}
        >
          ❌
        </span>
      )}

      {/* Layer 4.5: Crafting Progress Indicator */}
      {(() => {
        const building = buildingTile
          ? findBuildingByCharacter(buildingTile)
          : null;

        const isCraftingBuilding =
          building?.id === BuildingType.ASSEMBLER ||
          building?.id === BuildingType.FACTORY;

        const shouldShowResearchProgress =
          building?.showProgressBar &&
          building.id === BuildingType.RESEARCH_FACILITY;

        if (!building || (!isCraftingBuilding && !shouldShowResearchProgress)) {
          return null;
        }

        const centerPosition = findBuildingCenter(
          x,
          y,
          building,
          buildingLayer
        );
        if (!centerPosition) {
          return null;
        }

        const isCenterTile = centerPosition.x === x && centerPosition.y === y;

        if (isCraftingBuilding && isCenterTile) {
          const craftingInventory = getAssemblerInventory(
            assemblerInventoryLayer,
            buildingLayer,
            centerPosition
          );

          if (
            craftingInventory &&
            craftingInventory.craftingStartTick !== null &&
            craftingInventory.craftingProgress > 0
          ) {
            const progressPercent = Math.round(
              craftingInventory.craftingProgress * 100
            );
            const recipeName = craftingInventory.recipe?.name || 'Unknown';

            return (
              <span
                className='tile-layer crafting-progress-indicator'
                title={`Crafting ${recipeName}: ${progressPercent}%`}
              >
                <div
                  className='crafting-spinner'
                  style={{
                    background: `conic-gradient(#4ecdc4 ${progressPercent * 3.6}deg, rgba(78, 205, 196, 0.2) 0deg)`,
                  }}
                >
                  {progressPercent}%
                </div>
              </span>
            );
          }
        } else if (
          building.id === BuildingType.RESEARCH_FACILITY &&
          shouldShowResearchProgress &&
          isCenterTile
        ) {
          const researchState = getGameEngine().getResearchState();
          const selectedResearch = researchState.selectedResearch;
          if (selectedResearch) {
            const requirements = getNextResearchRequirements(
              researchState,
              selectedResearch
            );
            const totalRequired = requirements.reduce(
              (sum, requirement) => sum + requirement.amount,
              0
            );
            const rawProgress = getResearchProgress(
              researchState,
              selectedResearch
            );
            const cappedProgress =
              totalRequired > 0
                ? Math.min(rawProgress, totalRequired)
                : rawProgress;
            const progressPercent =
              totalRequired > 0
                ? Math.min(
                    Math.round((cappedProgress / totalRequired) * 100),
                    100
                  )
                : Math.min(Math.round(rawProgress * 100), 100);

            if (progressPercent > 0) {
              const researchName =
                RESEARCHES[selectedResearch]?.name ?? selectedResearch;
              return (
                <span
                  className='tile-layer crafting-progress-indicator'
                  title={`${researchName}: ${progressPercent}%`}
                >
                  <div
                    className='crafting-spinner'
                    style={{
                      background: `conic-gradient(#4ecdc4 ${progressPercent * 3.6}deg, rgba(78, 205, 196, 0.2) 0deg)`,
                    }}
                  >
                    {progressPercent}%
                  </div>
                </span>
              );
            }
          }
        }

        return null;
      })()}

      {/* Layer 5: Player */}
      {isPlayerPosition && (
        <span
          className={`tile-layer player-layer player ${showBeltPreview ? 'player-faded' : ''}`}
        >
          {GAME_CONFIG.PLAYER_CHARACTER}
        </span>
      )}

      {/* Layer 6: Building Preview */}
      {showBuildingPreview && selectedBuilding && (
        <span className='tile-layer preview-layer build-character'>
          {selectedBuilding.character}
        </span>
      )}

      {/* Layer 7: Belt Preview */}
      {showBeltPreview && beltPreviewBuilding && beltPreviewPosition && (
        <span
          className='tile-layer belt-preview-layer belt-preview-char'
          title={`${beltPreviewBuilding.name} (${playerFacing})`}
        >
          {getDirectionalBuildingCharacter(beltPreviewBuilding, playerFacing)}
        </span>
      )}
      {hasQuickBeltPreview && quickPreviewCharacter && (
        <span
          className={`tile-layer belt-preview-layer belt-preview-char quick-belt-preview-char${
            quickBeltPreviewInvalid ? ' quick-belt-preview-char-invalid' : ''
          }`}
          title={`Quick belt preview (${quickPreviewDirection})`}
        >
          {quickPreviewCharacter}
        </span>
      )}
    </div>
  );
};

// GameBoard component for rendering the game viewport
export const GameBoard: React.FC<GameBoardProps> = () => {
  // Use contexts for state management
  const uiState = useUIState();
  const mapState = useMapState();

  // Destructure values from contexts
  const {
    buildMode,
    selectedBuilding,
    buildMenuOpen,
    researchMenuOpen,
    inventoryOpen,
    helpMenuOpen,
    saveMenuOpen,
    debugMenuOpen,
    leftPanelOpen,
    achievementMenuOpen,
    buildingStatusMenuTarget,
    setBuildMenuState,
    setResearchMenuOpen,
    setInventoryOpen,
    setHelpMenuOpen,
    setSaveMenuOpen,
    setDebugMenuOpen,
    setLeftPanelOpen,
    setAchievementMenuOpen,
    exitBuildMode,
    closeBuildingStatusMenu,
  } = uiState;
  const {
    playerPos,
    cameraPos,
    getTile,
    gameMap,
    buildingLayer,
    beltContentLayer,
    assemblerInventoryLayer,
    rejectedItemsLayer,
    playerFacing,
    getTargetPosition,
    zoomLevel,
    resourceNodeMenu,
    closeResourceNodeMenu,
    deconstructBuilding,
    powerSatisfaction,
    researchState,
    quickBeltPreview,
    startQuickBeltPlacement,
    extendQuickBeltPlacement,
    finalizeQuickBeltPreview,
    cancelQuickBeltPlacement,
  } = mapState;

  const [mobileActionMenu, setMobileActionMenu] =
    React.useState<MobileActionMenuState | null>(null);
  const longPressTimeoutRef = React.useRef<number | null>(null);
  const longPressInfoRef = React.useRef<LongPressInfo | null>(null);
  const suppressTileClickRef = React.useRef(false);
  const touchDragStateRef = React.useRef<TouchDragState | null>(null);
  const quickBeltDragRef = React.useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    hasStarted: boolean;
    startPosition: Position;
    beltType: BeltType;
  } | null>(null);
  const quickBeltDragCleanupRef = React.useRef<(() => void) | null>(null);
  const quickBeltLastPositionRef = React.useRef<string | null>(null);
  const cancelQuickBeltPlacementRef = React.useRef(cancelQuickBeltPlacement);

  React.useEffect(() => {
    cancelQuickBeltPlacementRef.current = cancelQuickBeltPlacement;
  }, [cancelQuickBeltPlacement]);

  const clearLongPressDetection = React.useCallback(() => {
    if (longPressTimeoutRef.current !== null) {
      window.clearTimeout(longPressTimeoutRef.current);
      longPressTimeoutRef.current = null;
    }

    longPressInfoRef.current = null;
  }, []);

  const resetTouchDragState = React.useCallback(() => {
    touchDragStateRef.current = null;
  }, []);

  const closeMobileActionMenu = React.useCallback(() => {
    setMobileActionMenu(null);
    suppressTileClickRef.current = false;
    clearLongPressDetection();
  }, [clearLongPressDetection]);

  const openMobileActionMenu = React.useCallback((info: LongPressInfo) => {
    setMobileActionMenu({
      position: info.position,
      screenX: info.lastClientX,
      screenY: info.lastClientY,
      building: info.building,
      isBlocked: info.isBlocked,
    });
  }, []);

  const beltCapacityMultiplier = React.useMemo(
    () => getBeltCapacityMultiplier(researchState),
    [researchState]
  );

  const isQuickBeltSelected =
    buildMode &&
    selectedBuilding &&
    (selectedBuilding.id === BuildingType.BELT ||
      selectedBuilding.id === BuildingType.FAST_BELT);

  const quickBeltPreviewDirections = React.useMemo(() => {
    if (!quickBeltPreview) {
      return undefined;
    }

    const directionMap = new Map<string, PlayerFacing>();
    quickBeltPreview.segments.forEach(segment => {
      directionMap.set(
        `${segment.position.x},${segment.position.y}`,
        segment.direction
      );
    });

    return directionMap;
  }, [quickBeltPreview]);

  const quickBeltPreviewBuilding = React.useMemo(() => {
    if (!quickBeltPreview) {
      return null;
    }

    return BUILDINGS_BY_ID[quickBeltPreview.beltType] ?? null;
  }, [quickBeltPreview]);

  const quickBeltPreviewInvalid = Boolean(
    quickBeltPreview && !quickBeltPreview.isValid
  );

  const handleQuickBeltPointerMove = React.useCallback(
    (event: PointerEvent) => {
      const drag = quickBeltDragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) {
        return;
      }

      if (event.cancelable) {
        event.preventDefault();
      }

      if (!drag.hasStarted) {
        const deltaX = event.clientX - drag.startX;
        const deltaY = event.clientY - drag.startY;
        const distanceSquared = deltaX * deltaX + deltaY * deltaY;

        if (
          distanceSquared <
          LONG_PRESS_MOVE_TOLERANCE_PX * LONG_PRESS_MOVE_TOLERANCE_PX
        ) {
          const activeInfo = longPressInfoRef.current;
          if (activeInfo && activeInfo.pointerId === event.pointerId) {
            longPressInfoRef.current = {
              ...activeInfo,
              lastClientX: event.clientX,
              lastClientY: event.clientY,
            };
          }
          return;
        }

        cancelQuickBeltPlacement();
        startQuickBeltPlacement(drag.beltType, drag.startPosition);
        quickBeltLastPositionRef.current = `${drag.startPosition.x},${drag.startPosition.y}`;
        drag.hasStarted = true;
        clearLongPressDetection();
      }

      const targetElement = document.elementFromPoint(
        event.clientX,
        event.clientY
      ) as HTMLElement | null;

      if (!targetElement) {
        return;
      }

      const cellElement = targetElement.closest<HTMLDivElement>('.game-cell');
      if (!cellElement) {
        return;
      }

      const xAttr = cellElement.getAttribute('data-x');
      const yAttr = cellElement.getAttribute('data-y');
      if (!xAttr || !yAttr) {
        return;
      }

      const x = Number(xAttr);
      const y = Number(yAttr);
      if (Number.isNaN(x) || Number.isNaN(y)) {
        return;
      }

      const key = `${x},${y}`;
      if (quickBeltLastPositionRef.current === key) {
        return;
      }

      quickBeltLastPositionRef.current = key;
      extendQuickBeltPlacement({ x, y });
    },
    [
      cancelQuickBeltPlacement,
      clearLongPressDetection,
      extendQuickBeltPlacement,
      startQuickBeltPlacement,
    ]
  );

  const handleQuickBeltPointerUp = React.useCallback(
    (event: PointerEvent) => {
      const drag = quickBeltDragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) {
        return;
      }

      finalizeQuickBeltPreview();
      quickBeltDragCleanupRef.current?.();
      quickBeltDragCleanupRef.current = null;
      quickBeltDragRef.current = null;
      quickBeltLastPositionRef.current = null;
    },
    [finalizeQuickBeltPreview]
  );

  const handleQuickBeltPointerCancel = React.useCallback(
    (event: PointerEvent) => {
      const drag = quickBeltDragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) {
        return;
      }

      cancelQuickBeltPlacement();
      quickBeltDragCleanupRef.current?.();
      quickBeltDragCleanupRef.current = null;
      quickBeltDragRef.current = null;
      quickBeltLastPositionRef.current = null;
    },
    [cancelQuickBeltPlacement]
  );

  const handleTilePointerDown = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>, info: TilePointerInfo) => {
      if (isQuickBeltSelected) {
        const pointerType = event.pointerType;

        if (pointerType === 'mouse' && event.button !== 0) {
          return;
        }

        if (!event.isPrimary) {
          return;
        }

        if (!selectedBuilding) {
          return;
        }

        if (event.cancelable) {
          event.preventDefault();
        }

        suppressTileClickRef.current = true;
        quickBeltDragCleanupRef.current?.();
        quickBeltDragCleanupRef.current = null;

        const beltType = selectedBuilding.id as BeltType;
        quickBeltDragRef.current = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          hasStarted: pointerType === 'mouse',
          startPosition: info.position,
          beltType,
        };

        if (pointerType === 'mouse') {
          quickBeltLastPositionRef.current = `${info.position.x},${info.position.y}`;
          cancelQuickBeltPlacement();
          startQuickBeltPlacement(beltType, info.position);
        } else {
          quickBeltLastPositionRef.current = null;
        }

        window.addEventListener('pointermove', handleQuickBeltPointerMove, {
          passive: false,
        });
        window.addEventListener('pointerup', handleQuickBeltPointerUp, {
          passive: false,
        });
        window.addEventListener('pointercancel', handleQuickBeltPointerCancel, {
          passive: false,
        });

        quickBeltDragCleanupRef.current = () => {
          window.removeEventListener('pointermove', handleQuickBeltPointerMove);
          window.removeEventListener('pointerup', handleQuickBeltPointerUp);
          window.removeEventListener(
            'pointercancel',
            handleQuickBeltPointerCancel
          );
        };

        if (pointerType === 'mouse') {
          return;
        }
      }

      if (event.pointerType !== 'touch' && event.pointerType !== 'pen') {
        return;
      }

      if (
        buildMenuOpen !== false ||
        researchMenuOpen ||
        inventoryOpen ||
        helpMenuOpen ||
        saveMenuOpen ||
        debugMenuOpen ||
        achievementMenuOpen ||
        resourceNodeMenu
      ) {
        return;
      }

      closeMobileActionMenu();
      clearLongPressDetection();

      const pointerId = event.pointerId;
      const pointerInfo: LongPressInfo = {
        ...info,
        pointerId,
        startX: event.clientX,
        startY: event.clientY,
        lastClientX: event.clientX,
        lastClientY: event.clientY,
      };

      longPressInfoRef.current = pointerInfo;
      touchDragStateRef.current = {
        pointerId,
        lastClientX: event.clientX,
        lastClientY: event.clientY,
      };
      longPressTimeoutRef.current = window.setTimeout(() => {
        const activeInfo = longPressInfoRef.current;
        if (!activeInfo || activeInfo.pointerId !== pointerId) {
          return;
        }

        longPressTimeoutRef.current = null;
        longPressInfoRef.current = null;
        suppressTileClickRef.current = true;

        const activeQuickDrag = quickBeltDragRef.current;
        if (activeQuickDrag && activeQuickDrag.pointerId === pointerId) {
          quickBeltDragCleanupRef.current?.();
          quickBeltDragCleanupRef.current = null;
          quickBeltDragRef.current = null;
          quickBeltLastPositionRef.current = null;
          cancelQuickBeltPlacement();
        }

        openMobileActionMenu(activeInfo);
      }, LONG_PRESS_DURATION_MS);
    },
    [
      achievementMenuOpen,
      buildMenuOpen,
      cancelQuickBeltPlacement,
      clearLongPressDetection,
      closeMobileActionMenu,
      debugMenuOpen,
      handleQuickBeltPointerCancel,
      handleQuickBeltPointerMove,
      handleQuickBeltPointerUp,
      helpMenuOpen,
      inventoryOpen,
      isQuickBeltSelected,
      openMobileActionMenu,
      researchMenuOpen,
      resourceNodeMenu,
      saveMenuOpen,
      selectedBuilding,
      startQuickBeltPlacement,
    ]
  );

  const handleTilePointerUp = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const quickDrag = quickBeltDragRef.current;
      if (quickDrag && quickDrag.pointerId === event.pointerId) {
        return;
      }

      resetTouchDragState();
      const info = longPressInfoRef.current;
      if (info && info.pointerId === event.pointerId) {
        clearLongPressDetection();

        if (
          (event.pointerType === 'touch' || event.pointerType === 'pen') &&
          !suppressTileClickRef.current &&
          buildMode &&
          selectedBuilding
        ) {
          getGameEngine().setHoverTargetPosition(info.position);
          InputManager.getInstance().triggerTakeActionAt(info.position);
        }
      }
    },
    [buildMode, clearLongPressDetection, resetTouchDragState, selectedBuilding]
  );

  const handleTilePointerCancel = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const quickDrag = quickBeltDragRef.current;
      if (quickDrag && quickDrag.pointerId === event.pointerId) {
        return;
      }

      resetTouchDragState();
      const info = longPressInfoRef.current;
      if (info && info.pointerId === event.pointerId) {
        clearLongPressDetection();
      }
    },
    [clearLongPressDetection, resetTouchDragState]
  );

  const handleTilePointerLeave = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const quickDrag = quickBeltDragRef.current;
      if (quickDrag && quickDrag.pointerId === event.pointerId) {
        return;
      }

      resetTouchDragState();
      const info = longPressInfoRef.current;
      if (info && info.pointerId === event.pointerId) {
        clearLongPressDetection();
      }
    },
    [clearLongPressDetection, resetTouchDragState]
  );

  const handleTilePointerMove = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const quickDrag = quickBeltDragRef.current;
      if (quickDrag && quickDrag.pointerId === event.pointerId) {
        return;
      }

      const info = longPressInfoRef.current;
      if (info && info.pointerId === event.pointerId) {
        const deltaXFromStart = event.clientX - info.startX;
        const deltaYFromStart = event.clientY - info.startY;
        if (
          deltaXFromStart * deltaXFromStart +
            deltaYFromStart * deltaYFromStart >
          LONG_PRESS_MOVE_TOLERANCE_PX * LONG_PRESS_MOVE_TOLERANCE_PX
        ) {
          suppressTileClickRef.current = true;
          clearLongPressDetection();
        } else {
          longPressInfoRef.current = {
            ...info,
            lastClientX: event.clientX,
            lastClientY: event.clientY,
          };
        }
      }

      const dragState = touchDragStateRef.current;
      if (!dragState || dragState.pointerId !== event.pointerId) {
        return;
      }

      const deltaX = event.clientX - dragState.lastClientX;
      const deltaY = event.clientY - dragState.lastClientY;
      const absDeltaX = Math.abs(deltaX);
      const absDeltaY = Math.abs(deltaY);

      if (
        absDeltaX < TOUCH_DRAG_MOVE_THRESHOLD_PX &&
        absDeltaY < TOUCH_DRAG_MOVE_THRESHOLD_PX
      ) {
        return;
      }

      const moveX = absDeltaX >= absDeltaY ? Math.sign(deltaX) : 0;
      const moveY = absDeltaX > absDeltaY ? 0 : Math.sign(deltaY);

      suppressTileClickRef.current = true;
      InputManager.getInstance().triggerCommand(
        createMoveCommand(moveX, moveY)
      );

      touchDragStateRef.current = {
        pointerId: dragState.pointerId,
        lastClientX: event.clientX,
        lastClientY: event.clientY,
      };
    },
    [clearLongPressDetection]
  );

  React.useEffect(() => {
    return () => {
      clearLongPressDetection();
      resetTouchDragState();
      quickBeltDragCleanupRef.current?.();
      quickBeltDragCleanupRef.current = null;
      quickBeltDragRef.current = null;
      quickBeltLastPositionRef.current = null;
      cancelQuickBeltPlacementRef.current?.();
    };
  }, [clearLongPressDetection, resetTouchDragState]);

  React.useEffect(() => {
    if (!mobileActionMenu) {
      return;
    }

    if (
      buildMenuOpen !== false ||
      researchMenuOpen ||
      inventoryOpen ||
      helpMenuOpen ||
      saveMenuOpen ||
      debugMenuOpen ||
      achievementMenuOpen ||
      resourceNodeMenu
    ) {
      closeMobileActionMenu();
    }
  }, [
    mobileActionMenu,
    buildMenuOpen,
    researchMenuOpen,
    inventoryOpen,
    helpMenuOpen,
    saveMenuOpen,
    debugMenuOpen,
    achievementMenuOpen,
    resourceNodeMenu,
    closeMobileActionMenu,
  ]);

  const handleMobileBuild = React.useCallback(() => {
    if (!mobileActionMenu || mobileActionMenu.isBlocked) {
      return;
    }

    setBuildMenuState({ buildMenuOpen: true });
    closeMobileActionMenu();
  }, [closeMobileActionMenu, mobileActionMenu, setBuildMenuState]);

  const handleMobileDeconstruct = React.useCallback(() => {
    if (!mobileActionMenu || !mobileActionMenu.building) {
      return;
    }

    deconstructBuilding(
      mobileActionMenu.position.x,
      mobileActionMenu.position.y
    );
    closeMobileActionMenu();
  }, [closeMobileActionMenu, deconstructBuilding, mobileActionMenu]);

  const mobileActionMenuPosition = React.useMemo(() => {
    if (!mobileActionMenu) {
      return null;
    }

    if (typeof window === 'undefined') {
      return {
        left: mobileActionMenu.screenX,
        top: mobileActionMenu.screenY,
      };
    }

    const menuWidth = 220;
    const menuHeight = 140;
    const padding = 12;
    const left = Math.min(
      window.innerWidth - menuWidth - padding,
      Math.max(padding, mobileActionMenu.screenX - menuWidth / 2)
    );
    const top = Math.min(
      window.innerHeight - menuHeight - padding,
      Math.max(padding, mobileActionMenu.screenY - menuHeight / 2)
    );

    return { left, top };
  }, [mobileActionMenu]);

  // Extract getBuildingTargetLocation separately to handle potential undefined
  const getBuildingTargetLocation = mapState.getBuildingTargetLocation;

  const [powerOverlayActive, setPowerOverlayActive] = React.useState(false);

  React.useEffect(() => {
    let intervalId: number | undefined;
    let timeoutId: number | undefined;

    const triggerOverlay = () => {
      setPowerOverlayActive(true);
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
      timeoutId = window.setTimeout(() => {
        setPowerOverlayActive(false);
        timeoutId = undefined;
      }, 1000);
    };

    if (powerSatisfaction < 1) {
      triggerOverlay();
      intervalId = window.setInterval(() => {
        triggerOverlay();
      }, 2500);
    } else {
      setPowerOverlayActive(false);
    }

    return () => {
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
      }
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [powerSatisfaction]);

  const handleTileClick = React.useCallback(
    (position: Position, _building: Building | null) => {
      closeMobileActionMenu();
      let consumed = false;

      if (buildMenuOpen) {
        setBuildMenuState({
          buildMenuOpen: false,
          selectedCategory: null,
          selectedBuilding: null,
        });
        exitBuildMode();
        consumed = true;
      }

      if (researchMenuOpen) {
        setResearchMenuOpen(false);
        consumed = true;
      }

      if (inventoryOpen) {
        setInventoryOpen(false);
        consumed = true;
      }

      if (helpMenuOpen) {
        setHelpMenuOpen(false);
        consumed = true;
      }

      if (saveMenuOpen) {
        setSaveMenuOpen(false);
        consumed = true;
      }

      if (debugMenuOpen) {
        setDebugMenuOpen(false);
        consumed = true;
      }

      if (leftPanelOpen) {
        setLeftPanelOpen(false);
        consumed = true;
      }

      if (achievementMenuOpen) {
        setAchievementMenuOpen(false);
        consumed = true;
      }

      if (buildingStatusMenuTarget) {
        closeBuildingStatusMenu();
        consumed = true;
      }

      if (resourceNodeMenu) {
        closeResourceNodeMenu();
        consumed = true;
      }

      if (consumed) {
        return;
      }

      if (buildMode) return;

      const distance =
        Math.abs(position.x - playerPos.x) + Math.abs(position.y - playerPos.y);

      if (distance > 50) {
        return;
      }

      InputManager.getInstance().triggerTakeActionAt(position);
    },
    [
      achievementMenuOpen,
      buildMenuOpen,
      buildMode,
      buildingStatusMenuTarget,
      closeBuildingStatusMenu,
      debugMenuOpen,
      exitBuildMode,
      helpMenuOpen,
      inventoryOpen,
      leftPanelOpen,
      playerPos.x,
      playerPos.y,
      researchMenuOpen,
      saveMenuOpen,
      setAchievementMenuOpen,
      setBuildMenuState,
      setDebugMenuOpen,
      setHelpMenuOpen,
      setLeftPanelOpen,
      setResearchMenuOpen,
      setSaveMenuOpen,
      setInventoryOpen,
      resourceNodeMenu,
      closeResourceNodeMenu,
      closeMobileActionMenu,
    ]
  );

  const handleTileHover = React.useCallback((position: Position | null) => {
    getGameEngine().setHoverTargetPosition(position);
  }, []);
  // Don't render if essential data is not available
  if (!playerPos || !cameraPos || !getTile) {
    return (
      <div className='game-container'>
        <div className='game-board'>Loading...</div>
      </div>
    );
  }

  // Get target position for highlighting (always show, not just in build mode)
  const targetPos = getTargetPosition
    ? getTargetPosition(selectedBuilding || undefined)
    : null;

  // Calculate dynamic styles based on zoom level
  const baseFontSize = 12;
  const baseLineHeight = 12;
  const fontSize = Math.round(baseFontSize * zoomLevel);
  const lineHeight = Math.round(baseLineHeight * zoomLevel);

  const dynamicStyles: React.CSSProperties = {
    fontSize: `${fontSize}px`,
    lineHeight: `${lineHeight}px`,
    backgroundSize: `1ch ${lineHeight}px`,
  };

  // Adjust viewport size based on zoom level (fewer tiles when zoomed in for better performance)
  const zoomAdjustedWidth = Math.round(
    GAME_CONFIG.VIEWPORT_WIDTH / Math.max(1, zoomLevel * 0.75)
  );
  const zoomAdjustedHeight = Math.round(
    GAME_CONFIG.VIEWPORT_HEIGHT / Math.max(1, zoomLevel * 0.75)
  );

  // Calculate viewport bounds centered on player position
  const halfWidth = Math.floor(zoomAdjustedWidth / 2);
  const halfHeight = Math.floor(zoomAdjustedHeight / 2);

  // Calculate ideal viewport bounds centered on player
  let viewportLeft = playerPos.x - halfWidth;
  let viewportTop = playerPos.y - halfHeight;
  let viewportRight = viewportLeft + zoomAdjustedWidth - 1;
  let viewportBottom = viewportTop + zoomAdjustedHeight - 1;

  // Handle when world is smaller than viewport - keep player centered
  const worldTooSmallX = GAME_CONFIG.WORLD_WIDTH < zoomAdjustedWidth;
  const worldTooSmallY = GAME_CONFIG.WORLD_HEIGHT < zoomAdjustedHeight;

  if (worldTooSmallX) {
    // Center the world horizontally in the viewport
    viewportLeft = Math.floor(
      (GAME_CONFIG.WORLD_WIDTH - zoomAdjustedWidth) / 2
    );
    viewportRight = viewportLeft + zoomAdjustedWidth - 1;
  } else {
    // Clamp to world boundaries
    if (viewportLeft < 0) {
      viewportLeft = 0;
      viewportRight = Math.min(
        GAME_CONFIG.WORLD_WIDTH - 1,
        zoomAdjustedWidth - 1
      );
    } else if (viewportRight >= GAME_CONFIG.WORLD_WIDTH) {
      viewportRight = GAME_CONFIG.WORLD_WIDTH - 1;
      viewportLeft = Math.max(0, GAME_CONFIG.WORLD_WIDTH - zoomAdjustedWidth);
    }
  }

  if (worldTooSmallY) {
    // Center the world vertically in the viewport
    viewportTop = Math.floor(
      (GAME_CONFIG.WORLD_HEIGHT - zoomAdjustedHeight) / 2
    );
    viewportBottom = viewportTop + zoomAdjustedHeight - 1;
  } else {
    // Clamp to world boundaries
    if (viewportTop < 0) {
      viewportTop = 0;
      viewportBottom = Math.min(
        GAME_CONFIG.WORLD_HEIGHT - 1,
        zoomAdjustedHeight - 1
      );
    } else if (viewportBottom >= GAME_CONFIG.WORLD_HEIGHT) {
      viewportBottom = GAME_CONFIG.WORLD_HEIGHT - 1;
      viewportTop = Math.max(0, GAME_CONFIG.WORLD_HEIGHT - zoomAdjustedHeight);
    }
  }

  const viewport: React.ReactElement[] = [];

  // Add empty rows/cells for padding when world is smaller than viewport
  for (let y = viewportTop; y <= viewportBottom; y++) {
    const row: React.ReactElement[] = [];
    for (let x = viewportLeft; x <= viewportRight; x++) {
      // Check if this position is outside the world bounds (padding area)
      if (
        x < 0 ||
        x >= GAME_CONFIG.WORLD_WIDTH ||
        y < 0 ||
        y >= GAME_CONFIG.WORLD_HEIGHT
      ) {
        // Add empty padding cell
        row.push(
          <div key={`padding-${x}-${y}`} className='game-cell padding-cell'>
            <span className='tile-layer terrain-layer empty'>&nbsp;</span>
          </div>
        );
        continue;
      }

      // Create a multi-layer tile with key that includes assembler inventory
      const tileKey = `${x}-${y}`;
      const buildingKey = `${x},${y}`;
      const buildingTile = buildingLayer[buildingKey];
      const building = buildingTile
        ? findBuildingByCharacter(buildingTile)
        : null;

      // Add assembler recipe info to key for proper re-rendering
      let tileKeyWithRecipe = tileKey;
      if (
        building?.id === BuildingType.ASSEMBLER ||
        building?.id === BuildingType.FACTORY
      ) {
        // Find the center position of the assembler for inventory lookup
        const centerPosition = findBuildingCenter(
          x,
          y,
          building,
          buildingLayer
        );
        if (centerPosition) {
          const assemblerInventory = getAssemblerInventory(
            assemblerInventoryLayer,
            buildingLayer,
            centerPosition
          );
          tileKeyWithRecipe = `${tileKey}-${assemblerInventory?.recipe?.id || 'none'}`;
        }
      }

      row.push(
        <div key={tileKeyWithRecipe}>
          {renderMultiLayerTile(x, y, {
            getTile,
            gameMap,
            buildingLayer,
            beltContentLayer,
            assemblerInventoryLayer,
            rejectedItemsLayer,
            playerPos,
            targetPos,
            buildMode,
            selectedBuilding,
            playerFacing,
            getBuildingTargetLocation,
            onTileClick: handleTileClick,
            onTileHover: handleTileHover,
            powerOverlayActive,
            beltCapacityMultiplier,
            onTilePointerDown: handleTilePointerDown,
            onTilePointerUp: handleTilePointerUp,
            onTilePointerCancel: handleTilePointerCancel,
            onTilePointerLeave: handleTilePointerLeave,
            onTilePointerMove: handleTilePointerMove,
            suppressTileClick: suppressTileClickRef,
            quickBeltPreviewDirections,
            quickBeltPreviewBuilding,
            quickBeltPreviewInvalid,
          })}
        </div>
      );
    }
    viewport.push(
      <div key={y} className='game-row' style={{ height: `${lineHeight}px` }}>
        {row}
      </div>
    );
  }

  // Add class when world is smaller than viewport
  const smallWorldClass = worldTooSmallX || worldTooSmallY ? 'small-world' : '';

  return (
    <div className='game-container'>
      <div
        className={`game-board ${buildMode ? 'build-mode' : ''} ${smallWorldClass}`}
        style={dynamicStyles}
        onMouseLeave={() => handleTileHover(null)}
      >
        <div className='viewport-content'>{viewport}</div>
      </div>
      {mobileActionMenu && mobileActionMenuPosition && (
        <div
          className='mobile-action-menu-overlay'
          onClick={closeMobileActionMenu}
          role='presentation'
        >
          <div
            className='mobile-action-menu'
            role='dialog'
            aria-modal='true'
            style={{
              left: `${mobileActionMenuPosition.left}px`,
              top: `${mobileActionMenuPosition.top}px`,
            }}
            onClick={event => event.stopPropagation()}
          >
            <div className='mobile-action-menu__header'>
              <h3 className='mobile-action-menu__title'>Tile Actions</h3>
              <span className='mobile-action-menu__coordinates'>
                {mobileActionMenu.position.x}, {mobileActionMenu.position.y}
              </span>
            </div>
            {mobileActionMenu.building && (
              <p className='mobile-action-menu__subtitle'>
                {mobileActionMenu.building.name}
              </p>
            )}
            <div className='mobile-action-menu__options'>
              <button
                type='button'
                className='mobile-action-menu__button'
                onClick={handleMobileBuild}
                disabled={mobileActionMenu.isBlocked}
              >
                Build
              </button>
              <button
                type='button'
                className='mobile-action-menu__button'
                onClick={handleMobileDeconstruct}
                disabled={!mobileActionMenu.building}
              >
                Deconstruct
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
